import {
  FiMenu,
  FiSearch,
  FiSun,
  FiBell,
  FiUser,
  FiSettings,
  FiCreditCard,
  FiLogOut,
  FiChevronsLeft,
  FiChevronsRight,
  FiMoon,
  FiMonitor,
  FiX,
  FiClock,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { useEffect, useRef, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import { LogOut } from "lucide-react";
import api from "../../utils/axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const Navbar = ({ onToggleSidebar, onCollapseSidebar, isCollapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  const menuRef = useRef(null);
  const { darkMode, setDarkMode } = useTheme();

  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [onlineDrawerOpen, setOnlineDrawerOpen] = useState(false);

  const [pendingDeposits, setPendingDeposits] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const previousDepositsRef = useRef(null);
  const previousWithdrawalsRef = useRef(null);

  const fetchTransactionNotifications = async () => {
    if (user?.role !== "agent" && user?.role !== "backoffice") return;
    try {
      const res = await api.get("/getPendingCounts");
      if (res.data && res.data.status) {
        const deposits = res.data.pendingDeposits || 0;
        const withdrawals = res.data.pendingWithdrawals || 0;
        setPendingDeposits(deposits);
        setPendingWithdrawals(withdrawals);

        const Toast = Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer)
            toast.addEventListener('mouseleave', Swal.resumeTimer)
          }
        });

        // Check if there is a new pending deposit request since the last poll
        if (previousDepositsRef.current !== null && deposits > previousDepositsRef.current) {
          Toast.fire({
            icon: 'info',
            title: `New Pending Deposit`,
            html: `You have a new pending deposit request to review.`,
          });
        }

        // Check if there is a new pending withdrawal request since the last poll
        if (previousWithdrawalsRef.current !== null && withdrawals > previousWithdrawalsRef.current) {
          Toast.fire({
            icon: 'info',
            title: `New Pending Withdrawal`,
            html: `You have a new pending withdrawal request to review.`,
          });
        }

        previousDepositsRef.current = deposits;
        previousWithdrawalsRef.current = withdrawals;
      }
    } catch (err) {
      console.error("Error fetching pending counts for notification bell:", err);
    }
  };

  useEffect(() => {
    if (user?.role === "agent" || user?.role === "backoffice") {
      fetchTransactionNotifications();
      const interval = setInterval(fetchTransactionNotifications, 10000);
      return () => clearInterval(interval);
    }
  }, [user]);


  const fetchOnlinePlayers = async () => {
    try {
      const res = await api.get("/online-players");
      if (res.data && res.data.status) {
        setOnlinePlayers(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching online players:", err);
    }
  };

  useEffect(() => {
    fetchOnlinePlayers();
    const interval = setInterval(fetchOnlinePlayers, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "Just Now";
    const now = new Date();
    const loginDate = new Date(timestamp);
    const diffMs = now.getTime() - loginDate.getTime();
    if (diffMs < 0) return "Just Now";
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec} Sec Ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} Min Ago`;
    const diffHour = Math.floor(diffMin / 60);
    return `${diffHour} Hour Ago`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="h-16 bg-white border-b px-4 md:px-6 flex items-center justify-between relative dark:bg-secondary-900 dark:border-secondary-800">
      {/* Left: menu + search */}
      <div className="flex items-center gap-3">

        {/* Mobile sidebar toggle */}
        <button
          onClick={onToggleSidebar}
          className="md:hidden p-2 rounded-lg hover:bg-gray-100"
        >
          <FiMenu className="text-xl" />
        </button>

        {/* Desktop collapse toggle */}
        <button
          onClick={onCollapseSidebar}
          className="hidden md:block p-2 rounded-lg hover:bg-gray-100"
        >
          {isCollapsed ? (
            <FiChevronsRight className="text-xl" />
          ) : (
            <FiChevronsLeft className="text-xl" />
          )}
        </button>

        {/* Search input */}
        <div className="hidden sm:flex items-center bg-gray-100 rounded-lg px-3 py-2 gap-2">
          <FiSearch className="text-gray-500" />
          <input
            type="text"
            placeholder="Search..."
            className="bg-transparent text-sm outline-none w-40 md:w-64"
          />
          <span className="hidden md:inline text-xs text-gray-400">⌘K</span>
        </div>
      </div>

      {/* Right: actions + user */}
      <div className="flex items-center gap-5">

        {/* Player Online Button */}
        <button
          onClick={() => setOnlineDrawerOpen(true)}
          className="relative p-2 rounded-full hover:bg-gray-100 text-gray-700 dark:text-gray-200 transition"
          title="Online Players (Last 1 Hour)"
        >
          <FiMonitor className="text-lg" />
          {onlinePlayers.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[9px] font-bold flex items-center justify-center rounded-full bg-yellow-500 text-gray-900 border border-white">
              {onlinePlayers.length}
            </span>
          )}
        </button>

        {(user?.role === "agent" || user?.role === "backoffice") && (
          <>
            {/* Deposit Bell */}
            <button
              onClick={() => navigate("/recharge-list")}
              className="relative p-2 rounded-full hover:bg-gray-100 text-green-600 dark:text-green-400 transition"
              title="Pending Deposits"
            >
              <FiBell className="text-lg" />
              {pendingDeposits > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[9px] font-bold flex items-center justify-center rounded-full bg-green-500 text-white">
                  {pendingDeposits}
                </span>
              )}
            </button>

            {/* Withdrawal Bell */}
            <button
              onClick={() => navigate("/withdrawal-list")}
              className="relative p-2 rounded-full hover:bg-gray-100 text-red-600 dark:text-red-400 transition"
              title="Pending Withdrawals"
            >
              <FiBell className="text-lg" />
              {pendingWithdrawals > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[9px] font-bold flex items-center justify-center rounded-full bg-red-500 text-white">
                  {pendingWithdrawals}
                </span>
              )}
            </button>
          </>
        )}

        {/* Avatar + dropdown */}
        <div className="relative" ref={menuRef}>
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setOpenMenu((prev) => !prev)}
          >
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold">
              <img className="rounded-full" src="/avatar5.png" alt="" />
            </div>
          </div>

          {/* Dropdown menu */}
          {openMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border z-50">
              <div className="px-4 py-3 border-b">
                <p className="text-sm font-semibold">
                  {user?.name || 'Admin'}
                </p>
                <p className="text-xs text-gray-500">
                  {user?.email || 'admin@email.com'}
                </p>
              </div>

              <button
                onClick={logout}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 text-red-600"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ================= ONLINE PLAYERS DRAWER ================= */}
      {onlineDrawerOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 transition-opacity duration-300"
          onClick={() => setOnlineDrawerOpen(false)}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white dark:bg-secondary-900 border-l dark:border-secondary-800 shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${onlineDrawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        {/* Drawer Header */}
        <div className="px-4 py-4 border-b dark:border-secondary-800 flex items-center justify-between bg-gray-50 dark:bg-secondary-950">
          <div className="flex items-center gap-2">
            <FiMonitor className="text-blue-600 dark:text-blue-400" />
            <h3 className="font-bold text-gray-800 dark:text-gray-200 text-base">
              Player Online ({onlinePlayers.length})
            </h3>
          </div>
          <button
            onClick={() => setOnlineDrawerOpen(false)}
            className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-secondary-800 text-gray-500 dark:text-gray-400 transition"
          >
            <FiX className="text-lg" />
          </button>
        </div>

        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {onlinePlayers.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm font-medium">
              No online players in last 1 hour.
            </div>
          ) : (
            onlinePlayers.map((player) => (
              <div
                key={player.id}
                className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-secondary-850 transition cursor-pointer border border-transparent hover:border-gray-150 dark:hover:border-secondary-800"
              >
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-secondary-800 overflow-hidden flex items-center justify-center flex-shrink-0 border border-gray-200 dark:border-secondary-750">
                  <img
                    src={player.avatar || "/avatar5.png"}
                    alt="avatar"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "/avatar5.png";
                    }}
                  />
                </div>

                {/* Player details */}
                <div className="flex flex-col min-w-0">
                  <span className="font-semibold text-gray-800 dark:text-gray-800 text-sm truncate">
                    {player.name}
                  </span>
                  <span className="text-xs text-green-600 dark:text-green-800 font-medium">
                    Player Online
                  </span>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-1">
                    <FiClock className="w-3 h-3 flex-shrink-0" />
                    {formatRelativeTime(player.last_login)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
